import React, { useState } from 'react';
import {
  Box,
  TextField,
  IconButton,
  Paper,
  Tooltip,
  Typography,
  Button,
  Alert,
  CircularProgress
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import HomeIcon from '@mui/icons-material/Home';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

const SUGGESTED_URLS = [
  { label: 'Canva', url: 'https://www.canva.com' },
  { label: 'Google Drive', url: 'https://drive.google.com' },
  { label: 'ChatGPT', url: 'https://chat.openai.com' },
  { label: 'Gmail', url: 'https://mail.google.com' },
];

const BrowserToolPage = () => {
  const [url, setUrl] = useState('');
  const [activeUrl, setActiveUrl] = useState('');
  const [iframeKey, setIframeKey] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const handleNavigate = () => {
    let targetUrl = url.trim();
    if (!targetUrl) return;

    // Add https:// if no protocol
    if (!targetUrl.match(/^https?:\/\//i)) {
      targetUrl = 'https://' + targetUrl;
    }

    setActiveUrl(targetUrl);
    setLoading(true);
    setError(false);
    setIframeKey(prev => prev + 1);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleNavigate();
    }
  };

  const handleRefresh = () => {
    setIframeKey(prev => prev + 1);
    setLoading(true);
    setError(false);
  };

  const handleSuggestion = (suggestedUrl) => {
    setUrl(suggestedUrl);
    setActiveUrl(suggestedUrl);
    setLoading(true);
    setError(false);
    setIframeKey(prev => prev + 1);
  };

  const handleIframeLoad = () => {
    setLoading(false);
  };

  const handleIframeError = () => {
    setLoading(false);
    setError(true);
  };

  const openExternal = () => {
    if (activeUrl) {
      window.open(activeUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}>
      {/* URL Bar */}
      <Paper
        elevation={0}
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          px: 2,
          py: 1,
          display: 'flex',
          gap: 1,
          alignItems: 'center',
        }}
      >
        <Tooltip title="Back">
          <span>
            <IconButton size="small" disabled>
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Forward">
          <span>
            <IconButton size="small" disabled>
              <ArrowForwardIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Refresh">
          <IconButton size="small" onClick={handleRefresh} disabled={!activeUrl}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Home">
          <IconButton size="small" onClick={() => { setUrl(''); setActiveUrl(''); setError(false); }}>
            <HomeIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <TextField
          fullWidth
          size="small"
          placeholder="Enter URL (e.g., canva.com, drive.google.com)"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          onKeyPress={handleKeyPress}
          InputProps={{
            sx: { borderRadius: 2, bgcolor: 'background.paper' }
          }}
        />

        <IconButton color="primary" onClick={handleNavigate} disabled={!url.trim()}>
          <SearchIcon />
        </IconButton>

        <Tooltip title="Open in new tab">
          <span>
            <IconButton size="small" onClick={openExternal} disabled={!activeUrl}>
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Paper>

      {/* Content Area */}
      <Box sx={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
        {!activeUrl ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h5" fontWeight={600} color="primary" sx={{ mb: 1 }}>
              Web Browser
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Access Canva, Google Drive, or any web tool directly within Data Insights.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap', mt: 3 }}>
              {SUGGESTED_URLS.map(({ label, url: sugUrl }) => (
                <Button
                  key={sugUrl}
                  variant="outlined"
                  onClick={() => handleSuggestion(sugUrl)}
                  sx={{ minWidth: 120 }}
                >
                  {label}
                </Button>
              ))}
            </Box>
            <Alert severity="info" sx={{ mt: 4, maxWidth: 600, mx: 'auto', textAlign: 'left' }}>
              <Typography variant="caption" fontWeight={600}>
                Note: Some sites block iframe embedding
              </Typography>
              <Typography variant="caption" display="block" color="text.secondary">
                If a site doesn't load, use the "Open in new tab" button to access it in a separate window.
              </Typography>
            </Alert>
          </Box>
        ) : (
          <>
            {loading && (
              <Box
                sx={{
                  position: 'absolute',
                  top: '40%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  zIndex: 10,
                }}
              >
                <CircularProgress />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  Loading...
                </Typography>
              </Box>
            )}

            {error && (
              <Box
                sx={{
                  position: 'absolute',
                  top: '40%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: '90%',
                  maxWidth: 500,
                  zIndex: 10,
                }}
              >
                <Alert
                  severity="warning"
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      onClick={openExternal}
                      startIcon={<OpenInNewIcon />}
                    >
                      Open externally
                    </Button>
                  }
                >
                  <Typography variant="body2" fontWeight={600}>
                    This site doesn't allow embedding
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Click "Open externally" to view in a new tab, or try a different URL.
                  </Typography>
                </Alert>
              </Box>
            )}

            <iframe
              key={iframeKey}
              src={activeUrl}
              title="Web Browser"
              onLoad={handleIframeLoad}
              onError={handleIframeError}
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                display: error ? 'none' : 'block',
              }}
              allow="clipboard-read; clipboard-write; microphone; camera; geolocation; payment; autoplay"
              sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox allow-downloads"
            />
          </>
        )}
      </Box>
    </Box>
  );
};

export default BrowserToolPage;
